/**
 * Unified API Client
 * Single source of truth for all HTTP requests
 */

import { API_BASE } from "../config";
import { joinUrl } from "./url";
import { safeJsonParse, extractErrorMessage } from "./json";

// ============================================
// Types
// ============================================

export type TokenGetter = () => Promise<string | null>;

export type PaymentRequiredKey = "planRequired" | "sessionLimit";

export type ApiClientConfig = {
  baseUrl: string;
  getToken: TokenGetter;
  /** Called on 401 to force a token refresh. Return fresh token or null. */
  onTokenExpired?: () => Promise<string | null>;
  /** Called on 402 (entitlement gate) so the app can show a global toast. */
  on402?: (key: PaymentRequiredKey) => void;
  /** Optional: headers to include in every request */
  defaultHeaders?: Record<string, string>;
};

// ============================================
// Global 402 (payment required) notifier
// ============================================
// Many legacy call sites use raw fetch() instead of ApiClient. They can't be
// wired per-instance, so the app registers one global handler (AuthedApp) and
// those sites report 402s through these helpers.

let paymentRequiredHandler: ((key: PaymentRequiredKey) => void) | null = null;

export function setPaymentRequiredHandler(
  handler: ((key: PaymentRequiredKey) => void) | null
): void {
  paymentRequiredHandler = handler;
}

function keyFromErrorCode(code: unknown): PaymentRequiredKey {
  return code === "session_limit" ? "sessionLimit" : "planRequired";
}

/**
 * Report an HTTP 402 from a raw-fetch call site. `body` may be the parsed
 * JSON body or the raw response text (parsed here). No-op for other statuses.
 */
export function reportPaymentRequired(status: number, body: unknown): void {
  if (status !== 402) return;
  let parsed: any = body;
  if (typeof body === "string") {
    try {
      parsed = JSON.parse(body);
    } catch (e) {
      console.error("[api-client] unparseable 402 body", e);
      parsed = {};
    }
  }
  paymentRequiredHandler?.(keyFromErrorCode(parsed?.error));
}

/**
 * Report an HTTP 402 straight from a Response without consuming its body
 * (clone-based) — for call sites that hand the Response on to their caller,
 * e.g. useAuthedFetch's full-URL branch. No-op for other statuses.
 */
export async function reportPaymentRequiredResponse(response: Response): Promise<void> {
  if (response.status !== 402) return;
  const body = await response.clone().json().catch(() => ({}));
  paymentRequiredHandler?.(keyFromErrorCode(body?.error));
}

/**
 * Async ticks (plans/runCluster, plans/runFullPlan) surface entitlement
 * failures as HTTP 200 poll bodies: {"status":"error","error":"plan_required"}.
 * Poll error paths call this with the body's error code.
 */
export function reportPlanRequiredRunError(code: unknown): void {
  if (code === "plan_required") paymentRequiredHandler?.("planRequired");
}

export type RequestOptions = {
  /** Override the base URL for this request */
  baseUrl?: string;
  /** Additional headers for this request */
  headers?: Record<string, string>;
  /** AbortSignal for cancellation */
  signal?: AbortSignal;
  /** Skip authentication for this request */
  skipAuth?: boolean;
};

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string,
    public body?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ============================================
// API Client Class
// ============================================

export class ApiClient {
  private baseUrl: string;
  private getToken: TokenGetter;
  private onTokenExpired?: () => Promise<string | null>;
  private on402?: (key: PaymentRequiredKey) => void;
  private defaultHeaders: Record<string, string>;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl;
    this.getToken = config.getToken;
    this.onTokenExpired = config.onTokenExpired;
    this.on402 = config.on402;
    this.defaultHeaders = config.defaultHeaders || {};
  }

  /**
   * Build headers for a request
   */
  private async buildHeaders(
    hasBody: boolean,
    options?: RequestOptions
  ): Promise<Headers> {
    const headers = new Headers(this.defaultHeaders);

    // Add auth header
    if (!options?.skipAuth) {
      const token = await this.getToken();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
    }

    // Add content-type for requests with body
    if (hasBody && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    // Add custom headers
    if (options?.headers) {
      for (const [key, value] of Object.entries(options.headers)) {
        headers.set(key, value);
      }
    }

    return headers;
  }

  /**
   * Build the full URL for a request
   */
  private buildUrl(path: string, baseUrl?: string): string {
    const base = baseUrl || this.baseUrl;
    if (!base) throw new Error("API base URL is not configured");
    return joinUrl(base, path);
  }

  /**
   * Parse response and handle errors
   */
  private async handleResponse<T>(response: Response, url: string): Promise<T> {
    const text = await response.text();
    const data = safeJsonParse<T>(text, text as unknown as T);

    if (!response.ok) {
      // Entitlement gate — surface globally (body already parsed above, and
      // this also covers the post-401-retry response, unlike a check on the
      // first fetch alone).
      if (response.status === 402) {
        this.on402?.((data as any)?.error === "session_limit" ? "sessionLimit" : "planRequired");
      }
      const message = extractErrorMessage(
        text,
        `HTTP ${response.status} ${response.statusText}`
      );
      throw new ApiError(message, response.status, response.statusText, data);
    }

    return data;
  }

  /**
   * Core request method with single 401 retry via token refresh.
   */
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    const url = this.buildUrl(path, options?.baseUrl);
    const hasBody = body !== undefined;
    const headers = await this.buildHeaders(hasBody, options);

    const response = await fetch(url, {
      method,
      headers,
      body: hasBody ? JSON.stringify(body) : undefined,
      signal: options?.signal,
    });

    // On 401, force-refresh the token and retry once
    if (response.status === 401 && !options?.skipAuth && this.onTokenExpired) {
      await response.text().catch(() => {}); // drain body
      const freshToken = await this.onTokenExpired();
      if (freshToken) {
        const retryHeaders = await this.buildHeaders(hasBody, options);
        retryHeaders.set("Authorization", `Bearer ${freshToken}`);
        const retry = await fetch(url, {
          method,
          headers: retryHeaders,
          body: hasBody ? JSON.stringify(body) : undefined,
          signal: options?.signal,
        });
        return this.handleResponse<T>(retry, url);
      }
    }

    return this.handleResponse<T>(response, url);
  }

  async get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>("GET", path, undefined, options);
  }

  async post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>("POST", path, body, options);
  }

  async put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>("PUT", path, body, options);
  }

  async patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>("PATCH", path, body, options);
  }

  async delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>("DELETE", path, undefined, options);
  }

  /**
   * Fire the 402 handler for a raw-fetch response without consuming the body
   * (clone: the caller still needs the Response body).
   */
  private async notify402(response: Response): Promise<void> {
    if (response.status !== 402 || !this.on402) return;
    const body = await response.clone().json().catch(() => ({}));
    this.on402(body?.error === "session_limit" ? "sessionLimit" : "planRequired");
  }

  /**
   * Raw fetch with auth headers (for custom requests).
   * Also retries once on 401.
   */
  async fetch(path: string, init?: RequestInit, options?: RequestOptions): Promise<Response> {
    const url = this.buildUrl(path, options?.baseUrl);
    const headers = await this.buildHeaders(!!init?.body, options);

    // Merge with init headers
    if (init?.headers) {
      const initHeaders = new Headers(init.headers);
      initHeaders.forEach((value, key) => headers.set(key, value));
    }

    const response = await fetch(url, {
      ...init,
      headers,
      signal: options?.signal || init?.signal,
    });

    await this.notify402(response);

    if (response.status === 401 && !options?.skipAuth && this.onTokenExpired) {
      await response.text().catch(() => {});
      const freshToken = await this.onTokenExpired();
      if (freshToken) {
        const retryHeaders = new Headers(headers);
        retryHeaders.set("Authorization", `Bearer ${freshToken}`);
        const retry = await fetch(url, {
          ...init,
          headers: retryHeaders,
          signal: options?.signal || init?.signal,
        });
        await this.notify402(retry);
        return retry;
      }
    }

    return response;
  }
}

// ============================================
// Factory functions
// ============================================

/**
 * Create an API client for the main API (V2)
 */
export function createApiClient(getToken: TokenGetter): ApiClient {
  return new ApiClient({
    baseUrl: API_BASE,
    getToken,
  });
}

// ============================================
// Standalone functions (for backward compatibility)
// ============================================

/**
 * Simple GET request with token getter
 */
export async function apiGet<T>(
  path: string,
  getToken: TokenGetter,
  baseUrl = API_BASE
): Promise<T> {
  const client = new ApiClient({ baseUrl, getToken });
  return client.get<T>(path);
}

/**
 * Simple POST request with token getter
 */
export async function apiPost<T>(
  path: string,
  body: unknown,
  getToken: TokenGetter,
  baseUrl = API_BASE
): Promise<T> {
  const client = new ApiClient({ baseUrl, getToken });
  return client.post<T>(path, body);
}

/**
 * Simple PUT request with token getter
 */
export async function apiPut<T>(
  path: string,
  body: unknown,
  getToken: TokenGetter,
  baseUrl = API_BASE
): Promise<T> {
  const client = new ApiClient({ baseUrl, getToken });
  return client.put<T>(path, body);
}

/**
 * Simple DELETE request with token getter
 */
export async function apiDelete<T>(
  path: string,
  getToken: TokenGetter,
  baseUrl = API_BASE
): Promise<T> {
  const client = new ApiClient({ baseUrl, getToken });
  return client.delete<T>(path);
}
